import type {
  FareSliceDetailsV2,
  FareDetailsV2,
  Prices,
} from "@b2bportal/air-shopping-api";
import type { IFareData, IFlightFares } from "@overrides/types";

export const getFareTotalPrice = (fare: FareDetailsV2): Prices | undefined =>
  fare.paxPricings?.[0]?.pricing.total;

/**
 * Returns true when a fare slice's segments do not all share the same cabin
 * (mixed cabin itinerary).
 *
 * Compares `cabinClassName`, which the shopping service normalizes across
 * carriers — V2 shop payloads typically report one of "Economy", "Premium
 * Economy", "Business" or "First". That is an observed convention rather than
 * a contract: the field is a plain string with no enum behind it, and the same
 * field carries carrier-specific values in post-booking payloads. The
 * comparison is therefore case- and whitespace-insensitive to absorb
 * variation.
 *
 * Deliberately not `carrierCabinClass`, which is a carrier/GDS passthrough:
 * the same economy cabin arrives as "Y" from Sabre and "Economy" from AA, so
 * an interline or hacker fare would read as mixed cabin when it isn't.
 *
 * Segments with no cabin are ignored, so a missing value never produces a
 * false mixed-cabin warning.
 */
export const isMixedCabin = (fareSlice: FareSliceDetailsV2): boolean =>
  isMixedCabinSegments(fareSlice.fareDetails.segments);

/**
 * Segment-level variant of {@link isMixedCabin}, for callers that hold
 * segments rather than a shop fare slice (e.g. the booked itinerary segments
 * of a trip). Same comparison rules apply.
 */
export const isMixedCabinSegments = (
  segments: ReadonlyArray<{ cabinClassName?: string }>
): boolean => {
  const segmentCabinClasses = segments
    .map((segment) => segment.cabinClassName?.trim().toLowerCase())
    .filter(
      (cabinClassName) => cabinClassName != null && cabinClassName !== ""
    );

  return new Set(segmentCabinClasses).size > 1;
};

/**
 * Compare fares for sorting purposes.
 * Sorts by price, lowest to highest.
 *
 * When both fares have zero cash total (e.g., points-based pricing),
 * sorts by tax amount instead.
 *
 * @param fareA - First fare to compare
 * @param fareB - Second fare to compare
 */
export const compareFaresByPrice = (
  fareA: FareDetailsV2,
  fareB: FareDetailsV2
): number => {
  const pricingA = fareA.paxPricings?.[0]?.pricing;
  const pricingB = fareB.paxPricings?.[0]?.pricing;

  if (pricingA == null && pricingB == null) {
    return 0;
  } else if (pricingA == null) {
    return 1;
  } else if (pricingB == null) {
    return -1;
  }

  // Check if both fares have zero cash total (e.g., points-based pricing)
  const hasZeroTotal =
    pricingA.total.fiat.value === 0 && pricingB.total.fiat.value === 0;

  if (hasZeroTotal) {
    // When cash total is zero, sort by tax amount instead (lowest to highest)
    return pricingA.taxAmount.fiat.value - pricingB.taxAmount.fiat.value;
  } else {
    // When cash total is non-zero, sort by total price (lowest to highest)
    return pricingA.total.fiat.value - pricingB.total.fiat.value;
  }
};

export const filterWithFareShelfRatings = (
  selectedFareClassFilters: Array<number>,
  fareSliceRating: number
) => {
  const maxShelfRating = Math.max(...selectedFareClassFilters);

  return (
    selectedFareClassFilters.includes(fareSliceRating) ||
    fareSliceRating >= maxShelfRating
  );
};

export const getFareSliceRating = (fareSlice: FareSliceDetailsV2) =>
  typeof fareSlice?.fareShelf?.rating === "number" &&
  fareSlice.fareShelf.rating >= 0
    ? fareSlice.fareShelf.rating
    : -1;

export const getFilteredFareDetails = ({
  fareDetails,
  selectedFareClassFilters,
  sliceIndex,
  selectedFareId,
}: {
  fareDetails: FareDetailsV2[];
  selectedFareClassFilters: Array<number>;
  sliceIndex: number;
  selectedFareId?: string;
}): FareDetailsV2[] => {
  // userSelectedFare, defaults to first fare in list if none selected
  const selectedFare = fareDetails.find((f) => f.id === selectedFareId);

  const selectedFareRating =
    selectedFare?.slices[sliceIndex]?.fareShelf?.rating || -1;
  return fareDetails
    .filter((fare: FareDetailsV2) => {
      const fareSlice = fare.slices[sliceIndex];
      const fareSliceRating = getFareSliceRating(fareSlice);
      // Validate that fare slice has fareShelf defined
      if (fareSlice.fareShelf) {
        if (selectedFareClassFilters.length > 0) {
          // Filters applied
          return filterWithFareShelfRatings(
            selectedFareClassFilters,
            fareSliceRating
          );
        } else {
          // No filters applied
          if (selectedFareRating >= 0) {
            // If there's a selectedFare, show higher fares only
            return fareSliceRating >= selectedFareRating;
          }
        }
      }
      return true;
    })
    .sort(compareFaresByPrice);
};

export const getFilteredFares = (
  fares: IFlightFares,
  fareShelfFilter: Array<number>
): IFlightFares =>
  fareShelfFilter?.length == null
    ? structuredClone(fares)
    : Object.fromEntries(
        Object.entries(fares).filter(([, fare]) =>
          fareShelfFilter.includes(fare.fareShelf)
        )
      );

export const getCheapestFilteredFare = (
  fares: IFlightFares,
  fareShelfFilter: Array<number>
): IFareData => {
  const filteredFares = getFilteredFares(fares, fareShelfFilter);

  return Object.values(filteredFares).reduce((cheapest, fare) => {
    return fare.price.fiat.value < cheapest.price.fiat.value ? fare : cheapest;
  });
};
